import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { t, font } from "@realtyflows/ui";
import { getMe, switchPortal, saveToken, hasToken } from "../api";

/**
 * PortalDeepLink — V2 ADR-003 Phase 4.4.
 *
 * Resolves the URL pattern `/p/:slug/*` into a portal switch + redirect.
 * `:slug` is either the literal string "personal" (= the user's personal
 * portal, team_id null) or the team's `slug` column.
 *
 * Flow:
 *   1. Confirm the user is logged in (else redirect to /login with a
 *      ?next= back to this URL so they land in the right portal after).
 *   2. Fetch /me → list of memberships.
 *   3. Find the membership whose slug (or "personal") matches :slug.
 *   4. If it's not already the active portal, call switchPortal(team_id) →
 *      save the new JWT.
 *   5. Navigate to the inner path (everything after `/p/:slug/`), or to
 *      /dashboard if there isn't one.
 *
 * Purpose: shareable / bookmarkable per-portal URLs. Today's switcher
 * mutates in-session state; this is the URL-as-source-of-truth entry
 * point. Existing routes (/dashboard, /clients, /deals, etc.) are
 * unchanged — once we've landed on the inner path, the rest of the app
 * works exactly as before.
 *
 * Not in scope here (deferred to a later Phase 4.4 wave):
 *   - Updating every link in the app to use `/p/<slug>/` prefixes.
 *   - Updating the PortalSwitcher to navigate by URL instead of
 *     reloading.
 *   - URL rewriting on push so the bar always shows /p/<slug>/...
 */
export default function PortalDeepLink() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("resolving");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function go() {
      // Inner path: everything after "/p/:slug" in the URL. Honors a
      // trailing `?next=...` from the login redirect bounce.
      const innerFromUrl = location.pathname.replace(/^\/p\/[^/]+/, "") || "";
      const queryNext = new URLSearchParams(location.search).get("next");
      const targetPath = queryNext || (innerFromUrl + location.search) || "/dashboard";

      // Unauthenticated → bounce to login, asking it to come back here.
      if (!hasToken()) {
        const back = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?next=${back}`, { replace: true });
        return;
      }

      try {
        const me = await getMe();
        if (cancelled) return;
        const memberships = me.memberships || [];

        // Resolve :slug → membership row. "personal" is reserved.
        const wantPersonal = slug === "personal";
        const target = wantPersonal
          ? memberships.find((m) => m.team_id == null)
          : memberships.find((m) => (m.team_slug || "").toLowerCase() === slug.toLowerCase());

        if (!target) {
          // The user doesn't belong to that portal — could be a stale
          // link from a brokerage they left. Land them in their default
          // portal so they're not stuck on an error page.
          setStatus("not-a-member");
          setError(
            wantPersonal
              ? "You don't have a personal portal yet."
              : `You're not a member of "${slug}".`
          );
          setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
          return;
        }

        // If we're not already on the right portal, switch the JWT.
        const currentActive = me.active_team_id ?? null;
        const desiredActive = target.team_id ?? null;
        if (currentActive !== desiredActive) {
          setStatus("switching");
          const r = await switchPortal(target.team_id);
          if (cancelled) return;
          if (r?.token) {
            saveToken(r.token);
            // Mirror to localStorage so the rest of the app's
            // activeWorkspace state seeds correctly on the next render.
            try {
              if (desiredActive == null) localStorage.removeItem("rf_active_workspace");
              else localStorage.setItem("rf_active_workspace", String(desiredActive));
            } catch {}
          }
        }

        // Hard reload to the target — same pattern PortalSwitcher uses.
        // This guarantees every downstream component re-reads the JWT +
        // localStorage with the new portal in effect, without us having
        // to surgery the in-memory App state.
        window.location.href = targetPath;
      } catch (e) {
        if (cancelled) return;
        setError(e.message || "Couldn't switch portal");
        setStatus("error");
      }
    }

    go();
    return () => { cancelled = true; };
  }, [slug, location.pathname, location.search, navigate]);

  // Lightweight loading / error state — usually visible for <500ms.
  return (
    <div style={{
      minHeight: "100vh", background: t.pageBg, color: t.textPrimary,
      fontFamily: font.body, display: "flex",
      alignItems: "center", justifyContent: "center",
    }}>
      <div style={{ textAlign: "center" }}>
        {status === "error" || status === "not-a-member" ? (
          <>
            <div style={{ color: t.coral, fontSize: 16, marginBottom: 8 }}>{error}</div>
            <div style={{ color: t.textMuted, fontSize: 13 }}>Redirecting to your dashboard…</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontFamily: font.display, marginBottom: 6 }}>
              {status === "switching" ? "Switching portal…" : "Opening portal…"}
            </div>
            <div style={{ color: t.textMuted, fontSize: 13 }}>{slug}</div>
          </>
        )}
      </div>
    </div>
  );
}
